/**
 * service-mail shift client. Mirrors:
 *   apps/service-mail/src/modules/shift/shift.controller.ts
 *
 * RBAC:
 *   - clockIn/Out + listMine require Mail/clockInOut.
 *   - listAll, listActive require Mail/manageShifts.
 */
import { env } from '../env';

import { request } from './client';

export type ShiftStatus = 'OPEN' | 'CLOSED';

export interface ShiftDto {
  readonly id: string;
  readonly operatorId: string;
  readonly outletCode: string;
  readonly clockInAt: string;
  readonly clockOutAt: string | null;
  readonly clockInLat: string | null;
  readonly clockInLng: string | null;
  readonly breakMinutes: number;
  readonly notes: string | null;
  readonly status: ShiftStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListShiftsResponse {
  readonly items: readonly ShiftDto[];
  // eslint-disable-next-line upmp/no-number-money -- row count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly currentShift?: ShiftDto | null;
}

export interface ClockInBody {
  readonly outletCode: string;
  readonly clockInLat?: string;
  readonly clockInLng?: string;
  readonly notes?: string;
}

export interface ClockOutBody {
  readonly breakMinutes?: number;
  readonly notes?: string;
}

export interface ListShiftsQuery {
  readonly operatorId?: string;
  readonly outletCode?: string;
  readonly status?: ShiftStatus;
  readonly from?: string;
  readonly to?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

const baseUrl = (): string => env.serviceMailBaseUrl;

export async function clockIn(body: ClockInBody): Promise<ShiftDto> {
  return request<ShiftDto>(baseUrl(), '/v1/shifts/clock-in', { method: 'POST', body });
}

export async function clockOut(body: ClockOutBody = {}): Promise<ShiftDto> {
  return request<ShiftDto>(baseUrl(), '/v1/shifts/clock-out', { method: 'POST', body });
}

export async function listMyShifts(query: Omit<ListShiftsQuery, 'operatorId' | 'outletCode' | 'status'> = {}): Promise<ListShiftsResponse> {
  return request<ListShiftsResponse>(baseUrl(), '/v1/shifts/me', {
    method: 'GET',
    query: {
      from: query.from,
      to: query.to,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function listShifts(query: ListShiftsQuery = {}): Promise<ListShiftsResponse> {
  return request<ListShiftsResponse>(baseUrl(), '/v1/shifts', {
    method: 'GET',
    query: {
      operatorId: query.operatorId,
      outletCode: query.outletCode,
      status: query.status,
      from: query.from,
      to: query.to,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function listActiveShifts(outletCode?: string): Promise<readonly ShiftDto[]> {
  return request<readonly ShiftDto[]>(baseUrl(), '/v1/shifts/active', {
    method: 'GET',
    query: { outletCode },
  });
}
